import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Flex, VStack, Heading, Text, Center, Spinner } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../../hooks/useTranslation';
import type { Translations } from '../../../i18n/types';
import {
    useGetRegCustomerQuery,
    useCompleteRegCustomerMutation,
    getHttpStatus,
    isFetchBaseQueryError,
} from '../../../api';
import { CompanyLogoHeader } from '../../../components/auth/CompanyLogoHeader';
import { RegCustomerCompleteBadRequest } from '../../../api/registration/types';
import { RegistrationFormProgressBars } from './RegistrationFormProgressBars';
import { StepSelectTariff } from './steps/StepSelectTariff';
import { StepCompanyDetails } from './steps/StepCompanyDetails';
import { StepAddressDetails } from './steps/StepAddressDetails';
import { StepPersonalDetails } from './steps/StepPersonalDetails';
import { StepComplete } from './steps/StepComplete';
import { alertManager } from '../../../utils/alertManager';
import { modalManager } from '../../../utils/modalManager';

const slideVariants = {
    enter: (direction: number) => ({
        x: direction > 0 ? '100%' : '-100%',
        opacity: 1,
    }),
    center: {
        x: 0,
        opacity: 1,
    },
    exit: (direction: number) => ({
        x: direction > 0 ? '-100%' : '100%',
        opacity: 1,
    }),
};

const mapErrorFieldToLabel = (field: string, t: Translations) => {
    switch (field) {
        case 'first_name':
            return t.registration.form.firstNameLabel;
        case 'last_name':
            return t.registration.form.lastNameLabel;
        case 'gender':
            return t.registration.form.genderLabel;
        case 'phone':
            return t.registration.form.phoneLabel;
        case 'company_name':
            return t.registration.form.companyNameLabel;
        case 'activity':
            return t.registration.form.activityLabel;
        case 'inn':
            return t.registration.form.innLabel;
        case 'passport_file_url':
            return t.registration.form.passportFileLabel;
        case 'passport_reverse_file_url':
            return t.registration.form.passportReverseFileLabel;
        case 'personal_address':
            return t.registration.form.stepAddressDetails;
        case 'country':
            return t.registration.form.countryLabel;
        case 'city':
            return t.registration.form.cityLabel;
        case 'line':
            return t.registration.form.lineLabel;
        case 'postcode':
            return t.registration.form.postcodeLabel;
        default:
            return field;
    }
};

const RegistrationFormPage: React.FC = () => {
    const { t } = useTranslation({ setLangFromURL: true });
    const navigate = useNavigate();
    const location = useLocation();

    const { data: regCustomer, isLoading } = useGetRegCustomerQuery();
    const [completeRegCustomer] = useCompleteRegCustomerMutation();
    const [completeInvalidFields, setCompleteInvalidFields] = useState<string[]>([]);

    const [step, setStep] = useState(0);
    const [direction, setDirection] = useState(0);

    const registrationSteps = useMemo(
        () => [
            { Component: StepSelectTariff, title: t.registration.form.stepSelectTariff },
            { Component: StepCompanyDetails, title: t.registration.form.stepCompanyDetails },
            { Component: StepPersonalDetails, title: t.registration.form.stepPersonalDetails },
            { Component: StepAddressDetails, title: t.registration.form.stepAddressDetails },
            { Component: StepComplete, title: t.registration.form.stepComplete },
        ],
        [t],
    );
    const lastStepIndex = registrationSteps.length - 1;

    useEffect(() => {
        if (regCustomer?.status === 'review' && step !== lastStepIndex) {
            setDirection(0);
            setStep(lastStepIndex);
        }
        if (regCustomer?.status === 'verified') {
            navigate('/login');
        }
    }, [regCustomer?.status, step, navigate]);

    useEffect(() => {
        const targetPath = step === lastStepIndex ? '/registration/complete' : '/registration/form';
        if (location.pathname !== targetPath) {
            navigate({ pathname: targetPath, search: location.search }, { replace: true });
        }
    }, [step, lastStepIndex, location.pathname, location.search, navigate]);

    const completeInvalidFieldLabels = useMemo(
        () => completeInvalidFields.map((field) => mapErrorFieldToLabel(field, t)).filter(Boolean),
        [completeInvalidFields, t],
    );

    const tryCompleteRegistration = useCallback(async (): Promise<boolean> => {
        try {
            await completeRegCustomer().unwrap();
            setCompleteInvalidFields([]);
            return true;
        } catch (error) {
            const status = getHttpStatus(error);
            if (status === 400 && isFetchBaseQueryError(error)) {
                const data = error.data as RegCustomerCompleteBadRequest | undefined;
                const fields = Array.isArray(data?.fields)
                    ? data.fields
                          .filter((field): field is string => typeof field === 'string')
                          .filter(
                              (field) =>
                                  ![
                                      'id',
                                      'email',
                                      'office_id',
                                      'language',
                                      'subscription_id',
                                      'region',
                                  ].includes(field),
                          )
                    : [];
                setCompleteInvalidFields(fields);
                return false;
            }

            alertManager.error(t.registration.errors.serverError);
            return true;
        }
    }, [completeRegCustomer, t.registration.errors.serverError]);

    const showCompleteConfirmationModal = useCallback(
        (nextStep: number) => {
            modalManager.show({
                title: t.registration.form.completeRegistrationTitle,
                message: t.registration.form.completeRegistrationMessage,
                buttons: [
                    {
                        type: 'secondary',
                        text: t.registration.form.completeRegistrationCancelButton,
                        onClick: () => {
                            modalManager.remove();
                        },
                    },
                    {
                        type: 'success',
                        text: t.registration.form.completeRegistrationConfirmButton,
                        onClickAsync: async () => {
                            const isCompleteConfirmed = await tryCompleteRegistration();
                            if (isCompleteConfirmed) {
                                setDirection(1);
                                setStep(nextStep);
                            }
                            modalManager.remove();
                        },
                    },
                ],
            });
        },
        [t.registration.form, tryCompleteRegistration],
    );

    const goNext = useCallback(async () => {
        const nextStep = step + 1;
        if (nextStep === lastStepIndex) {
            showCompleteConfirmationModal(nextStep);
            return;
        } else {
            setCompleteInvalidFields([]);
        }

        setDirection(1);
        setStep(nextStep);
    }, [lastStepIndex, showCompleteConfirmationModal, step]);

    const goBack = useCallback(() => {
        if (step <= 0) return;
        setDirection(-1);
        setStep((s) => s - 1);
    }, [step]);

    const { Component: StepComponent, title: stepTitle } = registrationSteps[step];

    return (
        <Box
            minH="100dvh"
            bg="gray.100"
            display="flex"
            alignItems={{ base: 'stretch', md: 'center' }}
            justifyContent="center"
            overflow="hidden"
        >
            <Box
                w="full"
                maxW="lg"
                bg="white"
                p={{ base: 4, md: 6 }}
                display="flex"
                flexDirection="column"
                h={{ base: '100dvh', md: 'auto' }}
                minH={{ base: '100dvh', md: 'auto' }}
                overflow="hidden"
            >
                <Flex direction="column" flex={{ base: 1, md: 'unset' }} minH={0} gap={1}>
                    <VStack flexShrink={0} gap={2} align="stretch">
                        <CompanyLogoHeader />
                        {regCustomer?.email && (
                            <Text fontSize="md" color="gray.600" textAlign="center">
                                {`${t.registration.form.registrationFor} ${regCustomer.email}`}
                            </Text>
                        )}
                        {!isLoading && (
                            <>
                                <Box overflow="hidden" position="relative" minH="2.5rem" w="full">
                                    <AnimatePresence initial={false} custom={direction}>
                                        <motion.div
                                            key={step}
                                            custom={direction}
                                            variants={slideVariants}
                                            initial="enter"
                                            animate="center"
                                            exit="exit"
                                            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                                            style={{ position: 'absolute', inset: 0, width: '100%' }}
                                        >
                                            <Heading size="lg" color="gray.800" textAlign="center">
                                                {stepTitle}
                                            </Heading>
                                        </motion.div>
                                    </AnimatePresence>
                                </Box>
                                <RegistrationFormProgressBars
                                    currentStep={step}
                                    totalSteps={registrationSteps.length}
                                />
                                {completeInvalidFieldLabels.length > 0 && (
                                    <VStack align="stretch" gap={1}>
                                        <Text fontSize="sm" color="red.500" fontWeight="semibold">
                                            {t.registration.form.completeValidationFieldsTitle}
                                        </Text>
                                        <Text fontSize="sm" color="red.500">
                                            {completeInvalidFieldLabels.join(', ')}
                                        </Text>
                                    </VStack>
                                )}
                            </>
                        )}
                    </VStack>
                    <Box
                        flex={{ base: 1, md: 'unset' }}
                        minH={{ base: 0, md: '520px' }}
                        overflow="hidden"
                        position="relative"
                        w="full"
                    >
                        {isLoading ? (
                            <Center
                                h="full"
                                minH={{ base: '240px', md: '380px' }}
                                flexDirection="column"
                                gap={4}
                            >
                                <Spinner size="lg" color="yellow.600" />
                                <Text fontSize="md" color="gray.700" textAlign="center">
                                    {t.registration.form.loadingRegCustomerData}
                                </Text>
                            </Center>
                        ) : (
                            <AnimatePresence initial={false} custom={direction}>
                                <motion.div
                                    key={step}
                                    custom={direction}
                                    variants={slideVariants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        width: '100%',
                                        minHeight: '100%',
                                        height: '100%',
                                    }}
                                >
                                    <StepComponent onBack={goBack} onNext={goNext} />
                                </motion.div>
                            </AnimatePresence>
                        )}
                    </Box>
                </Flex>
            </Box>
        </Box>
    );
};

export default RegistrationFormPage;
